import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnSizingState,
  type ExpandedState,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';
import { Fragment, type ReactNode, useState } from 'react';

import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { cn } from '~shared/shadcn/lib/utils';
import { ScrollArea, ScrollBar } from '~shared/shadcn/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~shared/shadcn/ui/table';

import { EnrollioTableMobile } from './enrollio-table-mobile';
import {
  EnrollioTablePaginationCompact,
  EnrollioTablePaginationFull,
} from './enrollio-table-pagination';
import {
  EnrollioTableFilters,
  EnrollioTableToolbar,
  EnrollioTableTotal,
} from './enrollio-table-toolbar';
import type { EnrollioTableProps } from './enrollio-table.types';

const renderSlot = <TData,>(
  slot: ReactNode | ((table: TData) => ReactNode) | undefined,
  value: TData,
) => (typeof slot === 'function' ? slot(value) : slot);

/**
 * Control-only columns hold a checkbox, chevron or row number, so they must not
 * absorb the leftover width that the auto table layout spreads across columns.
 */
const SHRINK_TO_FIT_COLUMN_IDS = new Set(['select', 'expand', 'rownumber']);

const isShrinkToFitColumn = (columnId: string) =>
  SHRINK_TO_FIT_COLUMN_IDS.has(columnId.toLowerCase());

const isTrailingActionColumn = (columnId: string) =>
  columnId.toLowerCase().includes('action');

/**
 * An auto-layout table spreads surplus width across every column, so a narrow
 * control column still grows. Handing the surplus to one flexible column keeps
 * the control columns at their content width.
 */
const pickStretchColumnId = (columnIds: Array<string>) => {
  if (!columnIds.some(isShrinkToFitColumn)) return undefined;
  return columnIds.find(
    (columnId) =>
      !isShrinkToFitColumn(columnId) && !isTrailingActionColumn(columnId),
  );
};

export function EnrollioTable<TData extends object>({
  totalLabel,
  showTotal = true,
  filterHeader,
  filtersLayout = 'panel',
  data = [],
  columns = [],
  table: providedTable,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  pagination,
  onPaginationChange,
  rowSelection,
  onRowSelectionChange,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
  columnSizing,
  onColumnSizingChange,
  expanded,
  onExpandedChange,
  rowCount,
  manualPagination = false,
  manualSorting = false,
  manualFiltering = false,
  autoResetPageIndex,
  enableRowSelection,
  getRowId,
  getSubRows,
  getRowCanExpand,
  meta,
  toolbar,
  footer,
  paginationVariant = 'full',
  showSelectionCount = false,
  expandedRow,
  mobile = true,
  isUpdating = false,
  emptyContent = 'No results.',
  loadingLabel = 'Updating table',
  className,
  tableClassName,
  rowClassName,
  density = 'comfortable',
  ariaLabel = 'Data table',
}: EnrollioTableProps<TData>) {
  const isCompact = density === 'compact';

  // Passing an `onXChange` prop through as undefined overrides the table's
  // internal state updater, so every optional state piece needs an explicit
  // uncontrolled fallback (same pattern as pagination below) or it silently
  // dead-ends — e.g. the Columns toggle and uncontrolled filters/sorting.
  const [uncontrolledSorting, setUncontrolledSorting] =
    useState<SortingState>([]);
  const [uncontrolledColumnFilters, setUncontrolledColumnFilters] =
    useState<ColumnFiltersState>([]);
  const [uncontrolledPagination, setUncontrolledPagination] =
    useState<PaginationState>(() => ({
      pageIndex: 0,
      pageSize: getStoredPageSize(),
    }));
  const [uncontrolledRowSelection, setUncontrolledRowSelection] =
    useState<RowSelectionState>({});
  const [uncontrolledColumnVisibility, setUncontrolledColumnVisibility] =
    useState<VisibilityState>({});
  const [uncontrolledColumnOrder, setUncontrolledColumnOrder] =
    useState<ColumnOrderState>([]);
  const [uncontrolledColumnSizing, setUncontrolledColumnSizing] =
    useState<ColumnSizingState>({});
  const [uncontrolledExpanded, setUncontrolledExpanded] =
    useState<ExpandedState>({});

  const resolvedSorting = sorting ?? uncontrolledSorting;
  const resolvedOnSortingChange = onSortingChange ?? setUncontrolledSorting;
  const resolvedColumnFilters = columnFilters ?? uncontrolledColumnFilters;
  const resolvedOnColumnFiltersChange =
    onColumnFiltersChange ?? setUncontrolledColumnFilters;
  const resolvedPagination = pagination ?? uncontrolledPagination;
  const resolvedOnPaginationChange =
    onPaginationChange ?? setUncontrolledPagination;
  const resolvedRowSelection = rowSelection ?? uncontrolledRowSelection;
  const resolvedOnRowSelectionChange =
    onRowSelectionChange ?? setUncontrolledRowSelection;
  const resolvedColumnVisibility =
    columnVisibility ?? uncontrolledColumnVisibility;
  const resolvedOnColumnVisibilityChange =
    onColumnVisibilityChange ?? setUncontrolledColumnVisibility;
  const resolvedColumnOrder = columnOrder ?? uncontrolledColumnOrder;
  const resolvedOnColumnOrderChange =
    onColumnOrderChange ?? setUncontrolledColumnOrder;
  const resolvedColumnSizing = columnSizing ?? uncontrolledColumnSizing;
  const resolvedOnColumnSizingChange =
    onColumnSizingChange ?? setUncontrolledColumnSizing;
  const resolvedExpanded = expanded ?? uncontrolledExpanded;
  const resolvedOnExpandedChange = onExpandedChange ?? setUncontrolledExpanded;

  const internalTable = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting: resolvedSorting,
      columnFilters: resolvedColumnFilters,
      pagination: resolvedPagination,
      rowSelection: resolvedRowSelection,
      columnVisibility: resolvedColumnVisibility,
      columnOrder: resolvedColumnOrder,
      columnSizing: resolvedColumnSizing,
      expanded: resolvedExpanded,
    },
    onSortingChange: resolvedOnSortingChange,
    onColumnFiltersChange: resolvedOnColumnFiltersChange,
    onPaginationChange: resolvedOnPaginationChange,
    onRowSelectionChange: resolvedOnRowSelectionChange,
    onColumnVisibilityChange: resolvedOnColumnVisibilityChange,
    onColumnOrderChange: resolvedOnColumnOrderChange,
    onColumnSizingChange: resolvedOnColumnSizingChange,
    onExpandedChange: resolvedOnExpandedChange,
    rowCount,
    manualPagination,
    manualSorting,
    manualFiltering,
    autoResetPageIndex,
    enableRowSelection,
    getRowId,
    getSubRows,
    getRowCanExpand,
    meta,
  });
  const table = providedTable ?? internalTable;
  const rows = table.getRowModel().rows;
  const total =
    rowCount ??
    (manualFiltering ? data.length : table.getFilteredRowModel().rows.length);
  const stretchColumnId = pickStretchColumnId(
    table.getVisibleLeafColumns().map((column) => column.id),
  );
  const filterContent = renderSlot(filterHeader, table);
  const filters =
    filterContent === undefined ||
    filterContent === null ||
    filterContent === false
      ? null
      : filterContent;
  const showFiltersPanel = filters !== null && filtersLayout === 'panel';

  return (
    <section
      className={cn('flex min-h-0 min-w-0 flex-col', className)}
      aria-label={ariaLabel}
    >
      <EnrollioTableToolbar className={showFiltersPanel ? 'mb-3' : undefined}>
        <div className='flex flex-1 flex-wrap items-center gap-3'>
          {showTotal ? (
            <EnrollioTableTotal label={totalLabel} total={total} />
          ) : null}
          {showFiltersPanel ? null : filters}
        </div>
        {toolbar ? (
          <div className='flex flex-wrap gap-2'>
            {renderSlot(toolbar, table)}
          </div>
        ) : null}
      </EnrollioTableToolbar>

      {showFiltersPanel ? (
        <EnrollioTableFilters>{filters}</EnrollioTableFilters>
      ) : null}

      {mobile === true ? (
        <EnrollioTableMobile
          table={table}
          isUpdating={isUpdating}
          emptyContent={emptyContent}
          expandedRow={expandedRow}
          loadingLabel={loadingLabel}
          rowClassName={rowClassName}
        />
      ) : typeof mobile === 'function' ? (
        <div className='md:hidden'>{mobile(table, isUpdating)}</div>
      ) : null}

      <ScrollArea className='relative hidden min-w-0 flex-1 rounded-md border md:block'>
        <Table className={tableClassName} aria-label={ariaLabel}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    aria-sort={
                      header.column.getIsSorted() === 'asc'
                        ? 'ascending'
                        : header.column.getIsSorted() === 'desc'
                          ? 'descending'
                          : undefined
                    }
                    className={cn(
                      'sticky top-0 z-10 border-b bg-background',
                      isCompact && 'whitespace-nowrap',
                      isShrinkToFitColumn(header.column.id) &&
                        'w-px whitespace-nowrap',
                      header.column.id === stretchColumnId && 'w-full',
                    )}
                    style={
                      header.column.columnDef.size === undefined
                        ? undefined
                        : { width: header.getSize() }
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {rows.length > 0 ? (
              rows.map((row) => (
                <Fragment key={row.id}>
                  <TableRow
                    data-state={row.getIsSelected() ? 'selected' : undefined}
                    className={
                      typeof rowClassName === 'function'
                        ? rowClassName(row)
                        : rowClassName
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          isCompact && 'whitespace-nowrap py-2',
                          isShrinkToFitColumn(cell.column.id) &&
                            'w-px whitespace-nowrap',
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {expandedRow && row.getIsExpanded() ? (
                    <TableRow className='hover:bg-transparent'>
                      <TableCell
                        colSpan={row.getVisibleCells().length}
                        className='p-0'
                      >
                        {expandedRow(row)}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={Math.max(1, table.getVisibleLeafColumns().length)}
                  className='h-24 text-center text-muted-foreground'
                >
                  {isUpdating ? null : emptyContent}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <ScrollBar orientation='horizontal' />
        {isUpdating ? (
          <div
            className='absolute inset-0 flex items-center justify-center bg-background/70'
            role='status'
          >
            <Loader2 className='size-8 animate-spin' />
            <span className='sr-only'>{loadingLabel}</span>
          </div>
        ) : null}
      </ScrollArea>

      {paginationVariant === 'compact' ? (
        <EnrollioTablePaginationCompact table={table} />
      ) : (
        <EnrollioTablePaginationFull
          table={table}
          showSelection={showSelectionCount}
        />
      )}
      {footer ? renderSlot(footer, table) : null}
    </section>
  );
}
